import React, { useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { CheckCircle2, Clock3, Download, Link2, Loader2, Plus, Trash2, Upload } from 'lucide-react';
import {
  createTreasuryEntry,
  deleteTreasuryEntries,
  deleteTreasuryEntry,
  importBankStatements,
  setTreasuryEntriesStatus,
  updateTreasuryEntry,
} from '../../../../services/treasuryAPI';
import { isDateInSelectedPeriod } from '../../../placeholders/periodSummaryUtils';
import TreasuryTable3 from './TreasuryTable3';
import TreasuryBankReconcileModal from './TreasuryBankReconcileModal';
import {
  Pill,
  Stacked,
  TreasuryDrawer,
  accountLabel,
  buildFormStyles,
  downloadCsv,
  formatDate,
  money,
} from './treasuryUi';

const INCOME_COLOR = '#16a34a';
const EXPENSE_COLOR = '#ea580c';

const emptyForm = (accountId) => ({
  account_id: accountId ? String(accountId) : '',
  entry_date: new Date().toISOString().slice(0, 10),
  entry_type: 'expense',
  amount: '',
  description: '',
  category: '',
  document_number: '',
  channel: '',
  reference_code: '',
  running_balance: '',
  reconciliation_status: 'Pendiente',
  notes: '',
});

const TYPE_LABELS = { income: 'Ingreso', expense: 'Egreso' };
const SOURCE_LABELS = { manual: 'Manual', cartola: 'Cartola' };

const ledgerKey = (row) =>
  `${String(row.entry_date || '').slice(0, 10)}|${String(row.day_order ?? 0).padStart(6, '0')}|${String(
    row.id ?? 0
  ).padStart(10, '0')}`;

const formatElapsed = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return mins <= 0 ? `${secs}s` : `${mins}m ${String(secs).padStart(2, '0')}s`;
};

/**
 * Movimientos bancarios: tabla estilo Libro de ventas, formulario lateral e importación de cartolas.
 */
const TreasuryMovementsPanel = ({
  ui,
  isDarkMode = false,
  entries = [],
  accounts = [],
  loading = false,
  error = '',
  selectedPeriod = null,
  onChanged,
  onGoToAccounts,
}) => {
  const styles = buildFormStyles(ui);
  const [accountTab, setAccountTab] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [form, setForm] = useState(() => emptyForm(null));
  const [formError, setFormError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [importAccountId, setImportAccountId] = useState('');
  const [importFiles, setImportFiles] = useState([]);
  const [importError, setImportError] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [importElapsedSec, setImportElapsedSec] = useState(0);
  const [message, setMessage] = useState('');
  const [showReconcile, setShowReconcile] = useState(false);
  const [reconcileEntryId, setReconcileEntryId] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!isImporting) {
      setImportElapsedSec(0);
      return undefined;
    }
    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      setImportElapsedSec(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [isImporting]);

  useEffect(() => {
    if (accountTab !== 'all' && !accounts.some((account) => String(account.id) === accountTab)) {
      setAccountTab('all');
    }
  }, [accounts, accountTab]);

  const periodRows = useMemo(
    () =>
      selectedPeriod ? entries.filter((row) => isDateInSelectedPeriod(row.entry_date, selectedPeriod)) : entries,
    [entries, selectedPeriod]
  );

  const tabRows = useMemo(
    () => (accountTab === 'all' ? periodRows : periodRows.filter((row) => String(row.account_id) === accountTab)),
    [periodRows, accountTab]
  );

  const reconcileRows = useMemo(
    () => (accountTab === 'all' ? entries : entries.filter((row) => String(row.account_id) === accountTab)),
    [entries, accountTab]
  );

  const tabs = useMemo(() => {
    const counts = periodRows.reduce((acc, row) => {
      acc[row.account_id] = (acc[row.account_id] || 0) + 1;
      return acc;
    }, {});
    return {
      ariaLabel: 'Cuenta bancaria',
      active: accountTab,
      onChange: setAccountTab,
      items: [
        { key: 'all', label: 'Todas', count: periodRows.length },
        ...accounts.map((account) => ({
          key: String(account.id),
          label: account.account_name,
          fullLabel: [accountLabel(account), account.account_number].filter(Boolean).join(' · '),
          count: counts[account.id] || 0,
        })),
      ],
    };
  }, [accounts, periodRows, accountTab]);

  const categories = useMemo(
    () =>
      Array.from(new Set(entries.map((row) => String(row.category || '').trim()).filter(Boolean))).sort((a, b) =>
        a.localeCompare(b, 'es')
      ),
    [entries]
  );

  const totals = useMemo(
    () =>
      tabRows.reduce(
        (acc, row) => {
          const amount = Number(row.amount || 0);
          if (row.entry_type === 'income') acc.income += amount;
          else acc.expense += amount;
          return acc;
        },
        { income: 0, expense: 0 }
      ),
    [tabRows]
  );

  const columns = useMemo(
    () => [
      {
        key: 'date',
        label: 'Fecha',
        sortValue: ledgerKey,
        defaultDir: 'desc',
        render: (row) => (
          <Stacked
            top={formatDate(row.entry_date)}
            bottom={[row.entry_code, SOURCE_LABELS[row.source] || row.source].filter(Boolean).join(' · ')}
            topColor={ui.text}
            muted={ui.muted}
          />
        ),
      },
      {
        key: 'account',
        label: 'Cuenta',
        sortValue: (row) => accountLabel(row).toLowerCase(),
        render: (row) => (
          <Stacked
            top={row.account_name}
            bottom={[row.bank_name, row.account_number].filter(Boolean).join(' · ')}
            topColor={ui.text}
            muted={ui.muted}
          />
        ),
      },
      {
        key: 'description',
        label: 'Descripción',
        minWidth: 220,
        sortValue: (row) => String(row.description || '').toLowerCase(),
        render: (row) => (
          <Stacked
            top={row.description || '—'}
            bottom={[row.channel, row.document_number ? `Doc. ${row.document_number}` : '']
              .filter(Boolean)
              .join(' · ')}
            topColor={ui.text}
            muted={ui.muted}
          />
        ),
      },
      {
        key: 'category',
        label: 'Categoría',
        sortValue: (row) => String(row.category || '').toLowerCase(),
        render: (row) =>
          row.category ? (
            row.category
          ) : (
            <span style={{ color: ui.muted, fontStyle: 'italic' }}>Sin categoría</span>
          ),
      },
      {
        key: 'income',
        label: 'Ingreso',
        align: 'right',
        defaultDir: 'desc',
        sortValue: (row) => (row.entry_type === 'income' ? Number(row.amount || 0) : 0),
        render: (row) =>
          row.entry_type === 'income' ? (
            <span style={{ fontWeight: 700, color: INCOME_COLOR }}>{money(row.amount, row.currency)}</span>
          ) : null,
      },
      {
        key: 'expense',
        label: 'Egreso',
        align: 'right',
        defaultDir: 'desc',
        sortValue: (row) => (row.entry_type === 'expense' ? Number(row.amount || 0) : 0),
        render: (row) =>
          row.entry_type === 'expense' ? (
            <span style={{ fontWeight: 700, color: EXPENSE_COLOR }}>{money(row.amount, row.currency)}</span>
          ) : null,
      },
      {
        key: 'balance',
        label: 'Saldo',
        align: 'right',
        defaultDir: 'desc',
        sortValue: (row) => Number(row.computed_balance ?? 0),
        render: (row) => money(row.computed_balance, row.currency),
      },
      {
        key: 'document',
        label: 'Documento',
        minWidth: 160,
        sortValue: (row) =>
          String(row.linked_document?.doc_number || row.suggested_document?.doc_number || '').toLowerCase(),
        render: (row) => {
          const linked = row.linked_document;
          const suggested = row.suggested_document;
          if (linked) {
            return (
              <Stacked
                top={`${linked.document_type || linked.register_type_label || ''} ${linked.doc_number || ''}`.trim() || 'Vinculado'}
                bottom={linked.counterpart_name || linked.register_type_label}
                topColor={ui.text}
                muted={ui.muted}
              />
            );
          }
          if (suggested) {
            return (
              <Stacked
                top={`${suggested.document_type || suggested.register_type_label || ''} ${suggested.doc_number || ''}`.trim() || 'Sugerido'}
                bottom={`Sugerido · ${Math.max(0, Math.min(100, Math.round(Number(row.suggested_match_score || 0))))}%`}
                topColor={ui.text}
                muted={ui.muted}
              />
            );
          }
          return <span style={{ color: ui.muted }}>—</span>;
        },
      },
      {
        key: 'status',
        label: 'Conciliación',
        align: 'center',
        sortValue: (row) => String(row.reconciliation_status || ''),
        render: (row) => (
          <Pill
            label={row.reconciliation_status || 'Pendiente'}
            tone={row.reconciliation_status === 'Conciliado' ? 'success' : 'warning'}
            isDarkMode={isDarkMode}
          />
        ),
      },
    ],
    [ui.text, ui.muted, isDarkMode]
  );

  const filters = useMemo(
    () => [
      {
        key: 'type',
        label: 'Tipo',
        minWidth: 130,
        options: [
          { value: 'income', label: 'Ingresos' },
          { value: 'expense', label: 'Egresos' },
        ],
        predicate: (row, value) => row.entry_type === value,
      },
      {
        key: 'status',
        label: 'Conciliación',
        minWidth: 140,
        options: [
          { value: 'Pendiente', label: 'Pendiente' },
          { value: 'Conciliado', label: 'Conciliado' },
        ],
        predicate: (row, value) => (row.reconciliation_status || 'Pendiente') === value,
      },
      {
        key: 'category',
        label: 'Categoría',
        minWidth: 160,
        options: [
          { value: '__none__', label: 'Sin categoría' },
          ...categories.map((category) => ({ value: category, label: category })),
        ],
        predicate: (row, value) =>
          value === '__none__' ? !String(row.category || '').trim() : row.category === value,
      },
      {
        key: 'source',
        label: 'Origen',
        minWidth: 120,
        options: [
          { value: 'cartola', label: 'Cartola' },
          { value: 'manual', label: 'Manual' },
        ],
        predicate: (row, value) => row.source === value,
      },
    ],
    [categories]
  );

  const searchText = (row) =>
    [
      row.entry_code,
      row.entry_date,
      formatDate(row.entry_date),
      row.description,
      row.channel,
      row.document_number,
      row.category,
      row.account_name,
      row.bank_name,
      row.account_number,
      row.notes,
      row.amount,
      row.linked_document?.doc_number,
      row.linked_document?.counterpart_name,
      row.suggested_document?.doc_number,
    ].join(' ');

  const defaultAccountId = accountTab !== 'all' ? accountTab : accounts[0]?.id;

  const openCreate = () => {
    if (!accounts.length) {
      setMessage('Primero crea una cuenta bancaria en la pestaña "Cuentas bancarias".');
      return;
    }
    setEditingRow(null);
    setForm(emptyForm(defaultAccountId));
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (row) => {
    setEditingRow(row);
    setForm({
      account_id: String(row.account_id),
      entry_date: String(row.entry_date || '').slice(0, 10),
      entry_type: row.entry_type || 'expense',
      amount: String(row.amount ?? ''),
      description: row.description || '',
      category: row.category || '',
      document_number: row.document_number || '',
      channel: row.channel || '',
      reference_code: row.reference_code || '',
      running_balance:
        row.running_balance === null || row.running_balance === undefined ? '' : String(row.running_balance),
      reconciliation_status: row.reconciliation_status || 'Pendiente',
      notes: row.notes || '',
    });
    setFormError('');
    setShowForm(true);
  };

  const setField = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const handleSave = async () => {
    if (!form.account_id) return setFormError('Selecciona la cuenta bancaria.');
    if (!form.entry_date) return setFormError('La fecha es obligatoria.');
    const amount = Number(form.amount);
    if (!Number.isFinite(amount) || amount <= 0) return setFormError('El monto debe ser mayor a 0.');
    const payload = {
      ...form,
      account_id: Number(form.account_id),
      amount,
      running_balance: form.running_balance === '' ? null : Number(form.running_balance),
    };
    try {
      setIsSaving(true);
      setFormError('');
      if (editingRow) await updateTreasuryEntry(editingRow.id, payload);
      else await createTreasuryEntry(payload);
      setShowForm(false);
      onChanged?.();
    } catch (err) {
      setFormError(err.message || 'No se pudo guardar el movimiento.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (row) => {
    try {
      await deleteTreasuryEntry(row.id);
      onChanged?.();
    } catch (err) {
      window.alert(err.message || 'No se pudo eliminar el movimiento.');
    }
  };

  const runBulk = async (request, successText) => {
    try {
      const result = await request();
      setMessage(successText(result));
      onChanged?.();
      return true;
    } catch (err) {
      window.alert(err.message || 'No se pudo completar la acción.');
      return false;
    }
  };

  const bulkActions = [
    {
      key: 'reconcile',
      label: 'Marcar conciliado',
      icon: CheckCircle2,
      tone: 'success',
      onClick: (selected) =>
        runBulk(
          () => setTreasuryEntriesStatus(selected.map((row) => row.id), 'Conciliado'),
          (result) => `${result?.updated ?? selected.length} movimiento(s) marcados como conciliados.`
        ),
    },
    {
      key: 'pending',
      label: 'Marcar pendiente',
      icon: Clock3,
      onClick: (selected) =>
        runBulk(
          () => setTreasuryEntriesStatus(selected.map((row) => row.id), 'Pendiente'),
          (result) => `${result?.updated ?? selected.length} movimiento(s) marcados como pendientes.`
        ),
    },
    {
      key: 'delete',
      label: 'Eliminar',
      icon: Trash2,
      tone: 'danger',
      confirm: (count) => `¿Eliminar ${count} movimiento(s)?`,
      onClick: (selected) =>
        runBulk(
          () => deleteTreasuryEntries(selected.map((row) => row.id)),
          (result) => `${result?.deleted ?? selected.length} movimiento(s) eliminados.`
        ),
    },
  ];

  const handleExport = (rows) => {
    downloadCsv(
      `movimientos_bancarios_${new Date().toISOString().slice(0, 10)}.csv`,
      [
        'Código',
        'Fecha',
        'Banco',
        'Cuenta',
        'N° cuenta',
        'Descripción',
        'Canal/Sucursal',
        'N° documento',
        'Categoría',
        'Tipo',
        'Ingreso',
        'Egreso',
        'Saldo',
        'Moneda',
        'Conciliación',
        'Origen',
        'Notas',
      ],
      rows.map((row) => [
        row.entry_code,
        formatDate(row.entry_date),
        row.bank_name,
        row.account_name,
        row.account_number,
        row.description,
        row.channel,
        row.document_number,
        row.category,
        TYPE_LABELS[row.entry_type] || row.entry_type,
        row.entry_type === 'income' ? Number(row.amount || 0) : '',
        row.entry_type === 'expense' ? Number(row.amount || 0) : '',
        row.computed_balance ?? '',
        row.currency,
        row.reconciliation_status,
        SOURCE_LABELS[row.source] || row.source,
        row.notes,
      ])
    );
  };

  const openImport = () => {
    if (!accounts.length) {
      setMessage('Primero crea una cuenta bancaria en la pestaña "Cuentas bancarias" para importar su cartola.');
      return;
    }
    setImportAccountId(String(defaultAccountId || ''));
    setImportFiles([]);
    setImportError('');
    setShowImport(true);
  };

  const handleImport = async () => {
    if (!importAccountId) return setImportError('Selecciona la cuenta de destino.');
    if (!importFiles.length) return setImportError('Selecciona al menos un archivo.');
    const account = accounts.find((item) => String(item.id) === importAccountId);
    try {
      flushSync(() => {
        setShowImport(false);
        setIsImporting(true);
        setMessage('');
      });
      const result = await importBankStatements(Number(importAccountId), importFiles);
      const failed = (result?.files || []).filter((file) => file.error);
      const currency = account?.currency;
      const checked = result?.balance_check?.checked || 0;
      const openingChange = result?.opening_balance_change;
      const outOfPeriod =
        selectedPeriod &&
        result?.inserted > 0 &&
        !(
          isDateInSelectedPeriod(result.date_from, selectedPeriod) &&
          isDateInSelectedPeriod(result.date_to, selectedPeriod)
        );
      setMessage(
        [
          `Cartola importada en ${account ? accountLabel(account) : 'la cuenta'}: ${result.inserted} movimiento(s) nuevos, ${result.duplicates} ya existían y se omitieron.`,
          result.inserted > 0 ? `Rango: ${formatDate(result.date_from)} a ${formatDate(result.date_to)}.` : '',
          outOfPeriod ? 'Parte de los movimientos quedó fuera del período seleccionado.' : '',
          openingChange
            ? `Saldo inicial de la cuenta ajustado de ${money(openingChange.previous, currency)} a ${money(
                openingChange.current,
                currency
              )} según la cartola más antigua.`
            : '',
          result.inserted > 0 && checked
            ? `Saldos verificados: los ${checked} movimiento(s) de cartola cuadran con el saldo calculado.`
            : '',
          failed.length
            ? `No se pudieron leer: ${failed.map((file) => `${file.filename} (${file.error})`).join(' · ')}`
            : '',
        ]
          .filter(Boolean)
          .join(' ')
      );
      onChanged?.();
    } catch (err) {
      const text = err.message || 'No se pudo importar la cartola.';
      setMessage(text);
      window.alert(text);
    } finally {
      setIsImporting(false);
    }
  };

  const incomeLabel = money(totals.income);
  const expenseLabel = money(totals.expense);
  const subtitle = `${tabRows.length} movimiento${tabRows.length === 1 ? '' : 's'} · Ingresos ${incomeLabel} · Egresos ${expenseLabel} · Neto ${money(
    totals.income - totals.expense
  )}`;

  const pendingReconcileCount = tabRows.filter(
    (row) => !row.register_document_id && (row.reconciliation_status || 'Pendiente') !== 'Conciliado'
  ).length;
  const suggestedReconcileCount = tabRows.filter((row) => row.has_pending_document_match).length;

  const openReconcile = (entryId = null) => {
    setReconcileEntryId(entryId);
    setShowReconcile(true);
  };

  const importAccount = accounts.find((item) => String(item.id) === importAccountId);

  return (
    <div style={{ display: 'grid', gap: 12, position: 'relative' }}>
      {message ? (
        <p style={{ margin: 0, fontSize: 13, color: ui.muted }}>
          {message}
          {!accounts.length && onGoToAccounts ? (
            <>
              {' '}
              <button
                type="button"
                onClick={onGoToAccounts}
                style={{
                  border: 'none',
                  background: 'transparent',
                  padding: 0,
                  color: ui.interactive,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: 13,
                }}
              >
                Ir a Cuentas bancarias
              </button>
            </>
          ) : null}
        </p>
      ) : null}

      <TreasuryTable3
        rows={tabRows}
        loading={loading}
        error={error}
        isDarkMode={isDarkMode}
        title="Movimientos bancarios"
        subtitle={subtitle}
        columns={columns}
        defaultSort={{ key: 'date', dir: 'desc' }}
        searchPlaceholder="Descripción, N° documento, categoría, monto…"
        searchText={searchText}
        filters={filters}
        tabs={tabs}
        headerActions={[
          { key: 'import', label: 'Importar cartola', icon: Upload, onClick: openImport, busy: isImporting, busyLabel: 'Importando...' },
          { key: 'export', label: 'Exportar', icon: Download, onClick: handleExport, disabled: !tabRows.length },
          {
            key: 'reconcile',
            label: 'Conciliar',
            icon: Link2,
            onClick: () => openReconcile(null),
            disabled: !pendingReconcileCount,
            badge: suggestedReconcileCount || pendingReconcileCount || undefined,
            title: suggestedReconcileCount
              ? `${suggestedReconcileCount} movimiento(s) con documento sugerido`
              : pendingReconcileCount
                ? `${pendingReconcileCount} movimiento(s) pendientes de conciliar`
                : 'Sin movimientos pendientes',
          },
          { key: 'add', label: 'Nuevo movimiento', icon: Plus, variant: 'primary', onClick: openCreate },
        ]}
        bulkActions={bulkActions}
        onEdit={openEdit}
        onDelete={handleDelete}
        deleteConfirmText={(row) => `¿Eliminar el movimiento ${row.entry_code}?`}
        emptyText={
          accounts.length
            ? 'Sin movimientos en el período. Importa una cartola o registra un movimiento.'
            : 'Aún no hay cuentas bancarias. Créalas en la pestaña "Cuentas bancarias".'
        }
        minTableWidth={1080}
      />

      {showImport ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10000,
            background: ui.overlay || 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            boxSizing: 'border-box',
          }}
          onClick={() => setShowImport(false)}
        >
          <div
            style={{
              width: 'min(480px, 100%)',
              background: ui.panel,
              border: `1px solid ${ui.border}`,
              borderRadius: 12,
              padding: 20,
              boxShadow: '0 18px 48px rgba(0,0,0,0.25)',
              display: 'grid',
              gap: 14,
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <div>
              <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: ui.text }}>Importar cartola</p>
              <p style={{ margin: '4px 0 0', fontSize: 12, color: ui.muted, lineHeight: 1.45 }}>
                Banco de Chile / Edwards: cartola histórica o últimos movimientos descargados desde el portal
                (.xls, .xlsx o .csv). Los movimientos ya importados se omiten automáticamente.
              </p>
            </div>
            <label style={styles.label}>
              Cuenta de destino
              <select
                value={importAccountId}
                onChange={(event) => setImportAccountId(event.target.value)}
                style={styles.input}
              >
                <option value="">Selecciona</option>
                {accounts.map((account) => (
                  <option key={account.id} value={String(account.id)}>
                    {[accountLabel(account), account.account_number].filter(Boolean).join(' · ')}
                  </option>
                ))}
              </select>
            </label>
            {importAccount && importAccount.bank_name && !/chile|edwards/i.test(importAccount.bank_name) ? (
              <p style={{ margin: 0, fontSize: 12, color: '#b45309' }}>
                El lector está preparado para cartolas de Banco de Chile. Con otros bancos puede no reconocer
                las columnas.
              </p>
            ) : null}
            <div style={styles.label}>
              Archivos
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                multiple
                style={{ display: 'none' }}
                onChange={(event) => setImportFiles(Array.from(event.target.files || []))}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                style={{ ...styles.ghostBtn, justifyContent: 'center', borderStyle: 'dashed', height: 44 }}
              >
                <Upload size={15} />
                {importFiles.length
                  ? importFiles.map((file) => file.name).join(', ')
                  : 'Seleccionar cartolas (.xls, .xlsx, .csv)'}
              </button>
            </div>
            {importError ? <p style={{ margin: 0, color: ui.danger, fontSize: 12 }}>{importError}</p> : null}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" style={styles.ghostBtn} onClick={() => setShowImport(false)}>
                Cancelar
              </button>
              <button type="button" style={styles.primaryBtn} onClick={handleImport}>
                <Upload size={14} /> Importar
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {isImporting ? (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 12000,
            background: ui.overlay || 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            boxSizing: 'border-box',
          }}
        >
          <div
            style={{
              width: 'min(420px, 100%)',
              background: ui.panel,
              border: `1px solid ${ui.border}`,
              borderRadius: 12,
              padding: '28px 24px',
              boxShadow: '0 18px 48px rgba(0,0,0,0.25)',
              display: 'grid',
              gap: 12,
              justifyItems: 'center',
              textAlign: 'center',
            }}
          >
            <Loader2 size={36} color={ui.interactive} style={{ animation: 'treasury-import-spin 0.9s linear infinite' }} />
            <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: ui.text }}>Importando cartola</p>
            <p style={{ margin: 0, fontSize: 13, color: ui.muted }}>
              {importFiles.length === 1 ? `Procesando ${importFiles[0].name}…` : `Procesando ${importFiles.length} archivos…`}
            </p>
            <p style={{ margin: 0, fontSize: 12, color: ui.textSecondary }}>
              Tiempo transcurrido: {formatElapsed(importElapsedSec)}
            </p>
          </div>
          <style>
            {`
              @keyframes treasury-import-spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
              }
            `}
          </style>
        </div>
      ) : null}

      {showForm ? (
        <TreasuryDrawer
          ui={ui}
          title={editingRow ? `Editar movimiento ${editingRow.entry_code}` : 'Nuevo movimiento'}
          subtitle={
            editingRow?.source === 'cartola'
              ? 'Importado desde cartola. Puedes completar la categoría, la conciliación y las notas.'
              : 'Movimiento bancario registrado manualmente.'
          }
          error={formError}
          saving={isSaving}
          onClose={() => setShowForm(false)}
          onSave={handleSave}
        >
          <label style={styles.label}>
            Cuenta bancaria
            <select value={form.account_id} onChange={setField('account_id')} style={styles.input}>
              <option value="">Selecciona</option>
              {accounts.map((account) => (
                <option key={account.id} value={String(account.id)}>
                  {[accountLabel(account), account.account_number].filter(Boolean).join(' · ')}
                </option>
              ))}
            </select>
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <label style={styles.label}>
              Fecha
              <input type="date" value={form.entry_date} onChange={setField('entry_date')} style={styles.input} />
            </label>
            <label style={styles.label}>
              Tipo
              <select value={form.entry_type} onChange={setField('entry_type')} style={styles.input}>
                <option value="income">Ingreso (abono)</option>
                <option value="expense">Egreso (cargo)</option>
              </select>
            </label>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <label style={styles.label}>
              Monto
              <input type="number" min="0" value={form.amount} onChange={setField('amount')} style={styles.input} />
            </label>
            <label style={styles.label}>
              Saldo según cartola (opcional)
              <input
                type="number"
                value={form.running_balance}
                onChange={setField('running_balance')}
                style={styles.input}
              />
            </label>
          </div>
          <label style={styles.label}>
            Descripción
            <input value={form.description} onChange={setField('description')} style={styles.input} />
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <label style={styles.label}>
              N° documento
              <input value={form.document_number} onChange={setField('document_number')} style={styles.input} />
            </label>
            <label style={styles.label}>
              Canal / sucursal
              <input value={form.channel} onChange={setField('channel')} style={styles.input} />
            </label>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <label style={styles.label}>
              Categoría
              <input
                value={form.category}
                onChange={setField('category')}
                list="treasury-categories"
                placeholder="Ej: Proveedores, Clientes"
                style={styles.input}
              />
              <datalist id="treasury-categories">
                {categories.map((category) => (
                  <option key={category} value={category} />
                ))}
              </datalist>
            </label>
            <label style={styles.label}>
              Conciliación
              <select value={form.reconciliation_status} onChange={setField('reconciliation_status')} style={styles.input}>
                <option value="Pendiente">Pendiente</option>
                <option value="Conciliado">Conciliado</option>
              </select>
            </label>
          </div>
          <label style={styles.label}>
            Referencia interna
            <input
              value={form.reference_code}
              onChange={setField('reference_code')}
              placeholder="Ej: N° de factura o venta asociada"
              style={styles.input}
            />
          </label>
          <label style={styles.label}>
            Notas
            <textarea
              value={form.notes}
              onChange={setField('notes')}
              rows={3}
              style={{ ...styles.input, height: 'auto', padding: '8px 10px', resize: 'vertical' }}
            />
          </label>
        </TreasuryDrawer>
      ) : null}

      <TreasuryBankReconcileModal
        isOpen={showReconcile}
        onClose={({ linked } = {}) => {
          setShowReconcile(false);
          setReconcileEntryId(null);
          if (linked) {
            setMessage(`${linked} movimiento(s) conciliados con documentos de contabilidad.`);
            onChanged?.();
          } else {
            onChanged?.();
          }
        }}
        rows={reconcileRows}
        isDarkMode={isDarkMode}
        ui={ui}
        initialEntryId={reconcileEntryId}
      />
    </div>
  );
};

export default TreasuryMovementsPanel;
